import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SessionService } from '../../auth/session.service';
import { defineUserType } from '../../common/app.helpers';
import { CardModelType } from '../../common/custom.types';
import { IndividualRoles, UserTypes } from '../../components/profile/profile.enum';
import { getStatus } from '../../components/profile/user-profile/view-profile/regular-user-profile/regular-user-profile-helper';
import { CardModel, EntityStatus, ItemStatus } from '../../interfaces/views.interface';
import {
  AccountStatus,
  CommentSearchView,
  CommunityBasic,
  FullUser,
  PostBasic,
  ProjectBasic,
  UserBasic,
  UserRole,
} from '../../swagger-api';
import { CardBuilder, CommunityCardBuilder, ProjectCardBuilder } from './cards.builder';
import { CardType, InviteCardType } from './cards.types';

@Injectable({
  providedIn: 'root',
})
export class CardDirector {
  private userTypeMapper: { [key in UserTypes]: (data: FullUser) => CardType } = {
    individual: (data: FullUser) => this.getIndividualRole(data),
    organization: (_data) => CardType.ORGANIZATION,
    none: (_data) => CardType.USER,
  };

  private inviteCards: string[] = [InviteCardType.INVITE_TO_COMMUNITY, InviteCardType.INVITE_TO_PROJECT];

  private translateService = inject(TranslateService);
  private sessionService = inject(SessionService);

  /**
   * @description Returns new instanse of CardModel interface with setted properties
   *              that depends on/mapped by type.
   *              Also for 'User' type adapts its data and type first.
   * @param {CardModelType} data Card user info.
   * @param {CardType} type Card user info.
   * @param {EntityStatus} status Card Status.
   * @param {boolean} redirectToLoginIfNoSession Card should include login redirect url if user is not authenticated
   * @returns {CardModel}
   */
  public getCard(
    data: CardModelType,
    type: CardType | InviteCardType,
    status: EntityStatus,
    redirectToLoginIfNoSession: boolean,
    actionBtnHint: string,
  ): CardModel {
    const isAdmin = this.sessionService.isAdminSession();
    const isAuthenticated = this.sessionService.isSessionActive();

    if (type === CardType.COMMUNITY) {
      return new CommunityCardBuilder(this.translateService).toLegacyCardModel({
        data: data as CommunityBasic,
        view: 'browse',
        isAdmin,
        isAuthenticated,
        isRequariedAuthentication: redirectToLoginIfNoSession,
      });
    }

    if (type === CardType.PROJECT) {
      return new ProjectCardBuilder(this.translateService).toLegacyCardModel({
        data: data as ProjectBasic,
        view: 'browse',
        isAdmin,
        isAuthenticated,
        isRequariedAuthentication: redirectToLoginIfNoSession,
      });
    }

    let mainType: CardType;
    let additionalType = '';

    if (this.inviteCards.concat(CardType.USER).some((card: string) => card === type)) {
      const userData: FullUser = data as FullUser;
      additionalType = this.getAdditionalType(type, additionalType);
      mainType = this.getType(userData);
      status = this.userStatus(userData);
    } else {
      mainType = type as CardType;
    }

    if (type === CardType.POST) {
      const post = data as PostBasic;
      status = post.creator.account_status !== AccountStatus.ACTIVE ? this.userStatus(post.creator) : ItemStatus.EMPTY;
    }
    if (type === CardType.COMMENT) {
      const comment: CommentSearchView = data as CommentSearchView;

      status = this.userStatus(comment.comment_sender);
    }

    return new CardBuilder(
      data,
      mainType,
      additionalType,
      status,
      redirectToLoginIfNoSession,
      this.translateService,
      this.sessionService,
      actionBtnHint,
    ).create();
  }

  /**
   * @description Counts individual roles and returns individual user card type.
   * 1. If roles count is more than 1 (e.g., patient, doctor and researcher), 'User'
   *    will be returned as type (because all of the roles can't fit in the card header).
   * 2. Otherwise this single individual role name will be returned as type (one of the item
   *    from IndividualRoles enum).
   * @param {FullUser} data Card user info.
   * @returns {string}
   */
  private getIndividualRole(data: FullUser): CardType {
    const individualRolesKeys: string[] = Object.keys(IndividualRoles);
    const userInfoKeys: string[] = Object.keys(data);
    const roles: string[] = [];

    userInfoKeys.forEach((key: string) => {
      individualRolesKeys.includes(key) && roles.push(key);
    });

    if (roles.length === 1 && roles[0]) {
      const mainRole: string = roles[0];
      switch (mainRole) {
        case UserRole.Doctor:
          return CardType.DOCTOR;
        case UserRole.MedStaff:
          return CardType.MED_STAFF;
        case UserRole.Patient:
          return CardType.PATIENT;
        case UserRole.Researcher:
          return CardType.RESEARCHER;
        default:
          throw new Error('Unexpected user role');
      }
    } else {
      return CardType.USER;
    }
  }

  private getType(data: FullUser): CardType {
    return this.userTypeMapper[defineUserType(data)](data);
  }

  public isUserActive(user: UserBasic): boolean {
    return user.account_status !== AccountStatus.ACTIVE;
  }

  private getAdditionalType(type: string, additionalType: string): string {
    return this.inviteCards.some((card: string) => card === type) ? type : additionalType;
  }

  private userStatus(user: UserBasic): EntityStatus {
    return getStatus(user);
  }
}
